import { DocumentData, IndexableTypes, TypeConfig } from "./types"
import { SortByRenderState } from "instantsearch.js/es/connectors/sort-by/connectSortBy"
import {
  clearRefinements,
  hits,
  pagination,
  refinementList,
  searchBox,
  stats,
} from "instantsearch.js/es/widgets"
import { connectSortBy } from "instantsearch.js/es/connectors"
import { createInsightsMiddleware } from "instantsearch.js/es/middlewares"
import { debounce, filter } from "lodash-es"
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter"
import analyticsAction from "../analytics/analytics-action"
import analyticsError from "../analytics/analytics-error"
import instantsearch, { UiState } from "instantsearch.js"

type GlobalSearchProps = {
  collection: string
  clientKey: string
  searchInstance: string
}

/** Configuration object for each indexable type */
export const typesConfig: Record<IndexableTypes, TypeConfig> = {
  page: {
    rank: 1,
    label: { singular: "Page", plural: "Pages" },
  },
  "guide-and-toolkit": {
    rank: 2,
    label: { singular: "Guide or toolkit", plural: "Guides & toolkits" },
  },
  report: {
    rank: 3,
    label: { singular: "Report", plural: "Reports" },
  },
  webinar: {
    rank: 4,
    label: { singular: "On-demand webinar", plural: "On-demand webinars" },
  },
  video: {
    rank: 5,
    label: { singular: "Video", plural: "Videos" },
  },
  article: {
    rank: 6,
    label: { singular: "Blog", plural: "Blogs" },
  },
  podcast_episode: {
    rank: 7,
    label: { singular: "Podcast", plural: "Podcasts" },
  },
  announcement: {
    rank: 8,
    label: { singular: "Newsroom", plural: "Newsroom" },
  },
}

/** Ordered list of queryable fields */
const queryableFields: (keyof DocumentData)[] = [
  "heading",
  "description",
  "path",
]

export default function globalSearch(
  _el: HTMLElement,
  { collection, clientKey, searchInstance }: GlobalSearchProps
) {
  let searchInitialized = false
  const protocol = window.location.protocol.replace(/:$/, "")

  const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
    server: {
      apiKey: clientKey,
      nodes: [
        {
          path: "/api/search",
          host: window.location.hostname,
          port: parseInt(protocol === "https" ? "443" : window.location.port),
          protocol: protocol,
        },
      ],
    },
    additionalSearchParameters: {
      query_by: queryableFields.join(","),
      per_page: window.location.pathname.includes("/search") ? 10 : 5,
    },
  })

  let queryState = {
    queryString: "",
    numberOfResults: 0,
  }

  const queryMiddleware = () => {
    return {
      onStateChange: ({ uiState }: { uiState: UiState }) => {
        const collectionState = uiState[collection]
        const prevQuery = queryState.queryString
        const currentQuery = collectionState.query || ""

        if (prevQuery != currentQuery) {
          queryState.queryString = currentQuery
          const queryUpdatedEvent = new CustomEvent(
            `search:query:update:${searchInstance}`
          )
          document.dispatchEvent(queryUpdatedEvent)
        }
      },
      subscribe() {
        document.addEventListener(
          `search:query:update:${searchInstance}`,
          debounce(() => {
            if (!!queryState.queryString) {
              analyticsAction("Search queried", {
                "Search query": queryState.queryString,
                "Number of results": queryState.numberOfResults,
              })
            }
          }, 2000)
        )

        document.addEventListener(
          `search:query:update:${searchInstance}`,
          () => {
            const newTabButton = document.getElementById(
              `new-tab-${searchInstance}`
            )
            newTabButton?.setAttribute(
              "href",
              `/search?q=${queryState.queryString}`
            )
          }
        )
      },
      unsubscribe() {},
    }
  }

  const insightsMiddleware = createInsightsMiddleware({
    insightsClient: null,
    insightsInitParams: {
      useCookie: true,
    },
    onEvent: async (event: any) => {
      const payload = event.payload
      if ("eventName" in payload && payload.eventName === "Hit Clicked") {
        const hit = event.hits?.[0]

        if (!hit) return

        analyticsAction("Search clicked", {
          "Search query": queryState.queryString,
          "Number of results": queryState.numberOfResults,
          "Path of result": hit?.path,
          "Position of result": payload.positions && payload.positions[0],
        })

        fetch("/api/search/count-click", {
          method: "POST",
          headers: {
            "x-typesense-api-key": clientKey,
            "content-type": "application/json",
          },
          body: JSON.stringify({
            id: hit.id,
            collection: collection,
          }),
          keepalive: true,
        })
      }
    },
  })

  const renderHeadlessSortBy = (
    renderOptions: SortByRenderState,
    isFirstRender: boolean
  ) => {
    const { options, refine } = renderOptions

    if (isFirstRender) {
      document.addEventListener(`search:query:update:${searchInstance}`, () => {
        const query = queryState.queryString || ""
        if (query === "") {
          refine(options[0].value)
        } else {
          refine(options[1].value)
        }
      })
      refine(options[0].value)
    }
  }

  const headlessSortBy = connectSortBy(renderHeadlessSortBy)

  const loadingElement = document.querySelector(
    `.search-loading-${searchInstance}`
  )
  const pageSearchEl = document.getElementById("page-search")
  const desktopSearchToggle = document.querySelector(
    "button[aria-controls='menu-search']"
  )
  const mobileSearchToggle = document.querySelector(
    "button[aria-controls='mobile-search']"
  )

  const getSearchToggle = () => {
    if (searchInstance === "mobile") return mobileSearchToggle
    if (searchInstance === "desktop") return desktopSearchToggle
    return null
  }

  const initializeSearch = () => {
    if (searchInitialized) return
    loadingElement?.classList.add("hidden")

    const { searchClient: typesenseSearchClient } =
      typesenseInstantsearchAdapter

    const search = instantsearch({
      searchClient: typesenseSearchClient,
      indexName: collection,
      routing: {
        stateMapping: {
          stateToRoute(uiState) {
            const indexUiState = uiState[collection]

            return {
              q: indexUiState.query,
              type:
                indexUiState.refinementList && indexUiState.refinementList.type,
              page: indexUiState.page,
            }
          },
          routeToState(routeState) {
            return {
              [collection]: {
                query: routeState.q,
                refinementList: {
                  type: routeState.type ?? [],
                },
                page: routeState.page,
              },
            }
          },
        },
      },
    })

    search.addWidgets([
      searchBox({
        container: `#searchbox-${searchInstance}`,
        placeholder: "Search...",
        showLoadingIndicator: false,
        cssClasses: {
          submit: "hidden",
          reset:
            "absolute top-[3px] right-[3px] bg-white p-8 tablet:p-12 rounded-xl hover:[&>span]:opacity-70",
          input:
            "border p-8 tablet:p-12 rounded-xl text-16 bg-white w-full focus:outline-none focus:ring-2 focus:ring-black focus:ring-inset",
        },
        templates: {
          reset({ cssClasses }, { html }) {
            return html`<span>
              <svg
                class="size-20"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="none"
              >
                <path
                  fill="#171A1B"
                  fill-rule="evenodd"
                  d="M14.652 4.166 9.997 8.82 5.343 4.166l-1.179 1.18L8.82 9.999l-4.655 4.655 1.18 1.179 4.653-4.655 4.655 4.655 1.18-1.179L11.177 10l4.655-4.653-1.18-1.18Z"
                  clip-rule="evenodd"
                />
              </svg>
            </span>`
          },
        },
      }),

      refinementList({
        container: `#filters-${searchInstance}`,
        attribute: "type",
        cssClasses: {
          list: "flex flex-wrap desktop:flex-col w-full gap-x-24 gap-y-10 desktop:gap-y-16",
          noRefinementRoot: "peer",
        },
        sortBy: (a, b) => {
          const typeNameA = a.name as IndexableTypes
          const typeNameB = b.name as IndexableTypes
          return typesConfig[typeNameA].rank - typesConfig[typeNameB].rank
        },
        templates: {
          item(item, { html }) {
            const { url, label, count, isRefined } = item

            return html`
              <a href="${url}" class="flex items-center group/search-filters">
                <div class="relative flex">
                  <input
                    id="filter-${label}"
                    type="checkbox"
                    tabindex="-1"
                    class="peer border border-black border-px mr-10 rounded-full cursor-pointer h-24 w-24 inline-block scale-100 appearance-none group-hover/search-filters:border-2"
                    checked="${isRefined}"
                  />
                  <span
                    class="hidden peer-checked:block absolute top-4 left-4 size-16"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      fill="none"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill="#171a1b"
                        fill-rule="evenodd"
                        d="m8 15.42-5-5 1.41-1.41L8 12.59 15.59 5 17 6.42z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </span>
                </div>
                <span class="text-14 mr-6"
                  >${typesConfig[label as IndexableTypes].label.plural}</span
                >
                <div
                  class="bg-purple-200 text-10 font-semibold leading-none h-fit rounded-full px-[5px] py-[3px] opacity-70"
                >
                  ${count}
                </div>
              </a>
            `
          },
        },
      }),

      clearRefinements({
        container: `#clear-filters-${searchInstance}`,
        cssClasses: {
          disabledButton: "hidden",
          button: ["mt-20 desktop:mt-36"],
        },
        templates: {
          resetLabel({ hasRefinements }, { html }) {
            return html`<div
              class="${hasRefinements
                ? "flex gap-x-2 items-center text-12 font-medium hover:underline"
                : "h-0 hidden"}"
            >
              <svg
                class="size-20"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="none"
              >
                <path
                  fill="#171A1B"
                  fill-rule="evenodd"
                  d="M14.652 4.166 9.997 8.82 5.343 4.166l-1.179 1.18L8.82 9.999l-4.655 4.655 1.18 1.179 4.653-4.655 4.655 4.655 1.18-1.179L11.177 10l4.655-4.653-1.18-1.18Z"
                  clip-rule="evenodd"
                />
              </svg>
              Clear Filters
            </div>`
          },
        },
      }),

      hits({
        container: `#hits-${searchInstance}`,
        cssClasses: {
          list: "flex flex-col w-full gap-36 mt-16",
          emptyRoot: "hidden",
        },
        templates: {
          item(hit, { html }) {
            return html`
              <a href="${hit.path}" class="block group/search-result">
                <p class="mb-4">
                  <span
                    class="hit-title text-20 group-hover/search-result:underline"
                    >${hit.heading}</span
                  >
                  <span
                    class="hit-type ml-8 align-middle bg-purple-200 whitespace-nowrap font-semibold leading-none rounded-full h-fit text-10 px-[5px] py-[3px] opacity-70"
                  >
                    ${typesConfig[hit.type as IndexableTypes].label.singular}
                  </span>
                </p>
                <p class="hit-path text-12 opacity-70 mb-2">${hit.path}</p>
                <p
                  class="hit-description text-12 line-clamp-3 desktop:line-clamp-4"
                >
                  ${hit.description}
                </p>
              </a>
            `
          },
        },
      }),

      stats({
        container: `#stats-${searchInstance}`,
        cssClasses: {
          text: "text-12 opacity-70",
        },
        templates: {
          text(data, { html }) {
            let count = ""

            if (data.hasManyResults) {
              count += `${data.nbHits} results`
            } else if (data.hasOneResult) {
              count += `1 result`
            } else {
              count += `No results`
            }
            queryState.numberOfResults = data.nbHits

            return html` <p>${count} found in ${data.processingTimeMS}ms</p> `
          },
        },
      }),

      pagination({
        container: `#pagination-${searchInstance}`,
        cssClasses: {
          root: "mt-36",
          noRefinementRoot: "hidden",
          link: "text-black-70 hover:text-black hover:underline underline-offset-[4px] p-6",
          disabledItem: "hidden",
          selectedItem: "font-bold text-black underline underline-offset-[4px]",
        },
        templates: {
          previous:
            '<span class="button button--primary button--round"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none"><path fill="#171A1B" fill-rule="evenodd" d="M16 9.25H6.872l4.193-4.192L10 4l-6 6 6 6 1.057-1.057-4.185-4.193H16v-1.5Z" clip-rule="evenodd"/></svg></span>',
          next: '<span class="button button--primary button--round"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none"><path fill="#171A1B" fill-rule="evenodd" d="M4 10.75h9.128l-4.193 4.192L10 16l6-6-6-6-1.057 1.057 4.185 4.193H4v1.5Z" clip-rule="evenodd"/></svg></span>',
        },
        showFirst: false,
        showLast: false,
        padding: 1,
      }),

      headlessSortBy({
        items: [
          {
            label: "popularity",
            value: `${collection}/sort/popularity:desc,rank:asc`,
          },
          { label: "rank", value: `${collection}/sort/rank:asc` },
        ],
      }),
    ])

    search.on("render", () => {
      const errorWrappers = document.querySelectorAll(
        ".sitewide-search-wrapper"
      )
      const errorMessages = document.querySelectorAll(".sitewide-search-error")
      if (search.status === "error" && search.error) {
        const errorMessage = search.error.message
          .replace("Server said: ", "")
          .replace("Request failed with HTTP code ", "")

        analyticsError("Global search", errorMessage)

        errorWrappers.forEach(errorWrapper => {
          errorWrapper.classList.add("hidden")
        })

        errorMessages.forEach(errorMessage =>
          errorMessage.classList.remove("hidden")
        )
      }
    })

    search.use(queryMiddleware)
    search.use(insightsMiddleware)

    search.start()

    const activeInput = document.querySelector<HTMLInputElement>(
      `#searchbox-${searchInstance} .ais-SearchBox-input`
    )
    activeInput?.focus()

    // Set any initial query params loaded in the from URL params
    if (search.renderState[collection].searchBox?.query) {
      queryState.queryString = search.renderState[collection].searchBox?.query
    }
  }

  if (pageSearchEl?.checkVisibility() && searchInstance === "page") {
    initializeSearch()
    searchInitialized = true
  } else {
    const searchToggle = getSearchToggle()
    if (!searchToggle) return
    const observer = new MutationObserver(() => {
      const visibility = searchToggle?.getAttribute("aria-expanded") === "true"

      if (visibility && !searchInitialized) {
        initializeSearch()
        searchInitialized = true
      }
    })
    observer.observe(searchToggle, {
      attributes: true,
      attributeFilter: ["aria-expanded"],
    })
  }
}
